import React, { useState } from "react";
import { SpacingColumn } from "../../components/BuildingBlocks/SpacingColumn";
import { FrontQuestionFormCard } from "../../components/Front/Complex/FrontQuestionFormCard/FrontQuestionFormCard";
import FrontParagraphSmall from "../../components/Front/Simple/FrontParagraphSmall/FrontParagraphSmall";
import FrontInputGeneralComponent from "../../components/Front/Simple/FrontInputGeneralComponent/FrontInputGeneralComponent";
import FrontInputLabelAndErrorWrapComponent from "../../components/Front/Simple/FrontInputLabelAndErrorWrapComponent/FrontInputLabelAndErrorWrapComponent";
import { FrontFormPrimaryButton } from "../../components/Front/Simple/FrontFormPrimaryButton/FrontFormPrimaryButton";
import { FrontInfoBubble } from "../../components/Front/Simple/FrontInfoBubble/FrontInfoBubble";
import { WeightBold } from "../../components/WeightBold/WeightBold";
import { FrontColors } from "../../models/FrontColors";
import { FrontSpacing } from "../../models/FrontSpacing";
import { TContainerStateProps } from "../../state/TContainerStateProps";
import { TOnboardingTabStatus } from "../../components/Front/Simple/FrontOnboardingTab/FrontOnboardingTab";
import { ClientCaseIlaWaiverForm } from "../../../../domain/codecs/form/ClientCaseForm";

const FrontIlaWaiverContainer = (props: TContainerStateProps): JSX.Element => {
    const form = props.state.forms.client_case_page.children.ila_waiver || ClientCaseIlaWaiverForm.newDefault();
    const isStepComplete = form.children.is_step_complete;
    const signedName = form.edited.signed_name;
    const propertyAddress = props.state.forms.client_case_page.children.case.address;

    const [displayValidationErrorsIfPresent, setDisplayValidationErrorsIfPresent] = useState(false);

    const onNameChange = (value: string) => {
        setDisplayValidationErrorsIfPresent(false);
        props.dispatch({
            type: "CLIENT_ILA_WAIVER_CHANGE",
            payload: {
                ...form,
                edited: {
                    ...form.edited,
                    signed_name: value,
                    user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
                },
            },
        });
    };

    const onSubmit = () => {
        if (signedName.trim().length === 0) {
            setDisplayValidationErrorsIfPresent(true);
            return;
        }
        props.dispatch({ type: "CLIENT_ILA_WAIVER_SUBMIT", payload: undefined });
    };

    return (
        <FrontQuestionFormCard
            title="Confirm you're proceeding without legal advice"
        >
            <SpacingColumn spacing={FrontSpacing.LARGE_1}>

                <FrontParagraphSmall>
                    We've recommended that you <WeightBold>take your own independent legal advice</WeightBold> before this transfer of equity goes ahead. If you're happy to go ahead without it, please <WeightBold>read and confirm the statement below</WeightBold>.
                </FrontParagraphSmall>

                <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                    <FrontParagraphSmall>
                        I confirm that I am completing the transfer of equity of <WeightBold>{propertyAddress || "the Property"}</WeightBold> ("the Property"), and that I am being removed from its title.
                    </FrontParagraphSmall>

                    <FrontParagraphSmall>
                        I have been advised by Sail Legal Ltd that I should seek independent legal advice before proceeding with the transfer of equity, however I am happy to proceed without taking such advice.
                    </FrontParagraphSmall>

                    <FrontParagraphSmall>
                        On completion, Sail Legal has been instructed to pay me the agreed settlement sum in full and final settlement of my legal, beneficial and financial interest in the Property.
                    </FrontParagraphSmall>

                    <FrontParagraphSmall>
                        I understand that on receipt of my payment I will no longer have any financial, legal or beneficial interest in the Property.
                    </FrontParagraphSmall>

                    <FrontParagraphSmall>
                        On completion of the transfer, I confirm that I will no longer reside at the Property.
                    </FrontParagraphSmall>
                </SpacingColumn>

                {isStepComplete
                    ? (
                        <FrontInfoBubble color={FrontColors.PRIMARY_22}>
                            <FrontParagraphSmall>
                                <WeightBold>We've saved your confirmation.</WeightBold> Thank you — there's nothing more to do on this step.
                            </FrontParagraphSmall>
                        </FrontInfoBubble>
                    )
                    : (
                        <SpacingColumn spacing={FrontSpacing.MEDIUM_1}>
                            <FrontInputLabelAndErrorWrapComponent
                                label="Type your full name to confirm"
                                errorMessage="Please type your full name to confirm."
                                displayError={displayValidationErrorsIfPresent && signedName.trim().length === 0}
                            >
                                <FrontInputGeneralComponent
                                    inputType="text"
                                    value={signedName}
                                    displayError={displayValidationErrorsIfPresent && signedName.trim().length === 0}
                                    onChange={onNameChange}
                                />
                            </FrontInputLabelAndErrorWrapComponent>

                            <FrontFormPrimaryButton
                                label="I confirm and agree"
                                icon="done"
                                isDisabled={false}
                                onClick={onSubmit}
                            />
                        </SpacingColumn>
                    )
                }
            </SpacingColumn>
        </FrontQuestionFormCard>
    );
};

export const getIlaWaiverStatus = (props: TContainerStateProps): TOnboardingTabStatus => {
    const form = props.state.forms.client_case_page.children.ila_waiver;
    if (form && form.children.is_step_complete) {
        return "completed";
    }
    return "active";
};

export default FrontIlaWaiverContainer;
